import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import './JewelForm.css';

const MATERIAL_OPTIONS = [
  { value: 'gold', label: 'Gold' },
  { value: 'silver', label: 'Silver' },
  { value: 'gold_plated', label: 'Gold plated' },
  { value: 'fancy', label: 'Fancy' },
];

function materialUsesLivePricing(m) {
  return m === 'gold' || m === 'silver' || m === 'gold_plated';
}

export default function JewelForm({ jewel, onSubmit, onCancel, loading }) {
  const { admin } = useAuth();
  const [name, setName] = useState('');
  const [itemNumber, setItemNumber] = useState('');
  const [description, setDescription] = useState('');
  const [material, setMaterial] = useState('gold');
  const [categoryId, setCategoryId] = useState('');
  const [weightG, setWeightG] = useState('');
  const [fixedPrice, setFixedPrice] = useState('');
  const [wastageMode, setWastageMode] = useState('percent');
  const [wastageValue, setWastageValue] = useState('');
  const [makingMode, setMakingMode] = useState('percent');
  const [makingValue, setMakingValue] = useState('');
  const [isSold, setIsSold] = useState(false);
  const [image, setImage] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [extraFiles, setExtraFiles] = useState([]);
  const [extraPreviews, setExtraPreviews] = useState([]);
  const [existingExtras, setExistingExtras] = useState([]);
  const [categoryTree, setCategoryTree] = useState([]);

  useEffect(() => {
    fetch('/api/categories')
      .then((r) => r.json())
      .then(setCategoryTree)
      .catch(() => {});
  }, []);

  const categories = useMemo(() => {
    const flat = [];
    const applies = (c) => !c.material || c.material === material;
    for (const cat of categoryTree) {
      if (applies(cat)) flat.push(cat);
      for (const sub of cat.subcategories || []) {
        if (applies(sub)) {
          flat.push({ ...sub, name: `${cat.name} › ${sub.name}` });
        }
      }
    }
    return flat;
  }, [categoryTree, material]);

  useEffect(() => {
    if (!categoryId) return;
    const ok = categories.some((c) => String(c.id) === String(categoryId));
    if (!ok) setCategoryId('');
  }, [categories, categoryId]);

  useEffect(() => {
    return () => {
      extraPreviews.forEach((u) => URL.revokeObjectURL(u));
    };
  }, [extraPreviews]);

  useEffect(() => {
    if (!jewel) {
      setName('');
      setItemNumber('');
      setDescription('');
      setMaterial('gold');
      setCategoryId('');
      setWeightG('');
      setFixedPrice('');
      setWastageMode('percent');
      setWastageValue('');
      setMakingMode('percent');
      setMakingValue('');
      setIsSold(false);
      setImage(null);
      setPreviewUrl(null);
      setExtraFiles([]);
      setExtraPreviews([]);
      setExistingExtras([]);
      return;
    }
    setName(jewel.name || '');
      setItemNumber(jewel.item_number != null && jewel.item_number !== '' ? String(jewel.item_number) : '');
      setDescription(jewel.description || '');
      setMaterial(jewel.material || 'gold');
      setCategoryId(jewel.category_id || '');
      setWeightG(jewel.weight_g != null && jewel.weight_g !== '' ? String(jewel.weight_g) : '');
      setFixedPrice(jewel.fixed_price != null && jewel.fixed_price !== '' ? String(jewel.fixed_price) : '');
      const wf = jewel.wastage_fixed_inr != null && Number(jewel.wastage_fixed_inr) > 0;
      setWastageMode(wf ? 'fixed' : 'percent');
      setWastageValue(
        wf
          ? String(jewel.wastage_fixed_inr)
          : jewel.wastage_percent != null && jewel.wastage_percent !== ''
            ? String(jewel.wastage_percent)
            : ''
      );
      const mf = jewel.making_charge_fixed_inr != null && Number(jewel.making_charge_fixed_inr) > 0;
      setMakingMode(mf ? 'fixed' : 'percent');
      setMakingValue(
        mf
          ? String(jewel.making_charge_fixed_inr)
          : jewel.making_charge_percent != null && jewel.making_charge_percent !== ''
            ? String(jewel.making_charge_percent)
            : ''
      );
      setIsSold(!!jewel.is_sold);
      if (jewel.image_mime_type) {
        setPreviewUrl(`/api/jewels/${jewel.id}/image`);
      } else {
        setPreviewUrl(null);
      }
      setImage(null);
      setExtraFiles([]);
      setExtraPreviews([]);
      setExistingExtras(jewel.extra_images || []);
  }, [jewel]);

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    setImage(file);
    if (file) {
      setPreviewUrl(URL.createObjectURL(file));
    }
  };

  const handleExtrasChange = (e) => {
    const files = Array.from(e.target.files || []);
    setExtraFiles((prev) => [...prev, ...files]);
    setExtraPreviews((prev) => [...prev, ...files.map((f) => URL.createObjectURL(f))]);
    e.target.value = '';
  };

  const removeNewExtraAt = (idx) => {
    setExtraFiles((prev) => prev.filter((_, i) => i !== idx));
    setExtraPreviews((prev) => {
      URL.revokeObjectURL(prev[idx]);
      return prev.filter((_, i) => i !== idx);
    });
  };

  const removeExistingExtraClick = async (id) => {
    if (jewel?.id && admin) {
      try {
        await fetch(`/api/jewels/${jewel.id}/extra-images/${id}`, {
          method: 'DELETE',
          credentials: 'include',
        });
      } catch {
        // ignore
      }
    }
    setExistingExtras((prev) => prev.filter((x) => x.id !== id));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const formData = new FormData();
    formData.append('name', name);
    formData.append('item_number', itemNumber.trim());
    formData.append('description', description);
    formData.append('material', material);
    if (categoryId) formData.append('category_id', categoryId);
    formData.append('weight_g', weightG.trim() === '' ? '' : weightG);
    formData.append('fixed_price', fixedPrice.trim() === '' ? '' : fixedPrice);

    if (materialUsesLivePricing(material)) {
      if (wastageMode === 'percent') {
        formData.append('wastage_percent', wastageValue.trim() === '' ? '' : wastageValue);
        formData.append('wastage_fixed_inr', '');
      } else {
        formData.append('wastage_percent', '');
        formData.append('wastage_fixed_inr', wastageValue.trim() === '' ? '' : wastageValue);
      }
      if (makingMode === 'percent') {
        formData.append('making_charge_percent', makingValue.trim() === '' ? '' : makingValue);
        formData.append('making_charge_fixed_inr', '');
      } else {
        formData.append('making_charge_percent', '');
        formData.append('making_charge_fixed_inr', makingValue.trim() === '' ? '' : makingValue);
      }
    } else {
      formData.append('wastage_percent', '');
      formData.append('wastage_fixed_inr', '');
      formData.append('making_charge_percent', '');
      formData.append('making_charge_fixed_inr', '');
    }

    if (isSold) formData.append('is_sold', 'true');
    if (image) {
      formData.append('image', image);
    }
    extraFiles.forEach((f) => formData.append('extra_images', f));

    onSubmit(formData);
  };

  const showCharges = materialUsesLivePricing(material);

  return (
    <form className="jewel-form" onSubmit={handleSubmit}>
      <div className="form-group">
        <label htmlFor="name">Name *</label>
        <input id="name" type="text" value={name} onChange={(e) => setName(e.target.value)} required placeholder="e.g. Gold Necklace" />
      </div>

      <div className="form-group">
        <label htmlFor="itemNumber">Item number</label>
        <input
          id="itemNumber"
          type="text"
          value={itemNumber}
          onChange={(e) => setItemNumber(e.target.value)}
          placeholder="e.g. VG-1024 (unique, for search)"
          autoComplete="off"
        />
      </div>

      <div className="form-group">
        <label htmlFor="description">Description</label>
        <textarea id="description" value={description} onChange={(e) => setDescription(e.target.value)} rows="3" placeholder="Brief description" />
      </div>

      <div className="form-row">
        <div className="form-group">
          <label htmlFor="material">Material *</label>
          <select id="material" value={material} onChange={(e) => setMaterial(e.target.value)}>
            {MATERIAL_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>

        <div className="form-group">
          <label htmlFor="category">Category</label>
          <select id="category" value={categoryId} onChange={(e) => setCategoryId(e.target.value)}>
            <option value="">— None —</option>
            {categories.map((c) => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="form-row">
        <div className="form-group">
          <label htmlFor="weightG">Weight (grams)</label>
          <input
            id="weightG"
            type="number"
            step="0.001"
            min="0"
            value={weightG}
            onChange={(e) => setWeightG(e.target.value)}
            placeholder="For weight-based pricing (gold / silver / gold plated)"
          />
        </div>
        <div className="form-group">
          <label htmlFor="fixedPrice">Fixed price (₹)</label>
          <input
            id="fixedPrice"
            type="number"
            step="1"
            min="0"
            value={fixedPrice}
            onChange={(e) => setFixedPrice(e.target.value)}
            placeholder="Overrides weight-based price when set"
          />
        </div>
      </div>

      {showCharges && (
        <>
          <div className="jewel-form-charge-block">
            <span className="jewel-form-charge-label">Wastage</span>
            <div className="jewel-form-mode-row">
              <label><input type="radio" name="wastageMode" checked={wastageMode === 'percent'} onChange={() => setWastageMode('percent')} /> Percent (%)</label>
              <label><input type="radio" name="wastageMode" checked={wastageMode === 'fixed'} onChange={() => setWastageMode('fixed')} /> Fixed (₹)</label>
            </div>
            <input
              type="number"
              step="0.01"
              min="0"
              value={wastageValue}
              onChange={(e) => setWastageValue(e.target.value)}
              placeholder={wastageMode === 'percent' ? 'e.g. 8' : 'e.g. 500'}
              className="jewel-form-charge-input"
            />
          </div>

          <div className="jewel-form-charge-block">
            <span className="jewel-form-charge-label">Making charges</span>
            <div className="jewel-form-mode-row">
              <label><input type="radio" name="makingMode" checked={makingMode === 'percent'} onChange={() => setMakingMode('percent')} /> Percent (%)</label>
              <label><input type="radio" name="makingMode" checked={makingMode === 'fixed'} onChange={() => setMakingMode('fixed')} /> Fixed (₹)</label>
            </div>
            <input
              type="number"
              step="0.01"
              min="0"
              value={makingValue}
              onChange={(e) => setMakingValue(e.target.value)}
              placeholder={makingMode === 'percent' ? 'e.g. 12 — applied after wastage' : 'e.g. 1500'}
              className="jewel-form-charge-input"
            />
          </div>
        </>
      )}

      <p className="jewel-form-hint">
        Gold, silver, and gold plated: price is weight × live rate (gold rate for plated), then wastage, then making charges, unless you set a fixed price. Fancy uses fixed price only.
      </p>

      <label className="jewel-form-checkbox">
        <input
          type="checkbox"
          checked={isSold}
          onChange={(e) => setIsSold(e.target.checked)}
        />
        <span>Mark as sold (hidden from customers until unchecked)</span>
      </label>

      <div className="form-group">
        <label htmlFor="image">Primary image {!jewel && '*'}</label>
        <input id="image" type="file" accept="image/jpeg,image/png,image/webp,image/gif" onChange={handleImageChange} />
        {previewUrl && (
          <div className="image-preview">
            <img src={previewUrl} alt="Preview" />
          </div>
        )}
      </div>

      <div className="form-group">
        <label htmlFor="extra_images">Additional images (optional)</label>
        <input
          id="extra_images"
          type="file"
          accept="image/jpeg,image/png,image/webp,image/gif"
          multiple
          onChange={handleExtrasChange}
        />
        <div className="jewel-form-extra-grid">
          {existingExtras.map((ex) => (
            <div key={ex.id} className="jewel-form-extra-tile">
              <img src={`/api/jewel-extra-images/${ex.id}`} alt="" />
              <button type="button" className="jewel-form-extra-remove" onClick={() => removeExistingExtraClick(ex.id)}>Remove</button>
            </div>
          ))}
          {extraPreviews.map((url, idx) => (
            <div key={url} className="jewel-form-extra-tile">
              <img src={url} alt="" />
              <button type="button" className="jewel-form-extra-remove" onClick={() => removeNewExtraAt(idx)}>Remove</button>
            </div>
          ))}
        </div>
      </div>

      <div className="form-actions">
        <button type="submit" className="btn btn-primary" disabled={loading}>
          {loading ? 'Saving...' : jewel ? 'Update Jewel' : 'Add Jewel'}
        </button>
        {onCancel && (
          <button type="button" className="btn btn-secondary" onClick={onCancel}>Cancel</button>
        )}
      </div>
    </form>
  );
}
